import { tagsApi } from '@/lib/api';
import { NOYAU_ORDER } from '@/lib/noyaux';
import { useWordsNav } from '@/lib/words-nav';
import {
  DATE_PRESETS,
  DATE_PRESET_LABELS,
  type DatePreset,
  hasActiveFilters,
} from '@/lib/words-query';
import type { NoyauKey, WordTag } from '@/schemas/word';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';

// French colour names for the noyau swatch accessible labels.
const NOYAU_COLOR_LABELS: Record<NoyauKey, string> = {
  bleunuit: 'Bleu nuit',
  bleuciel: 'Bleu ciel',
  vert: 'Vert',
  jaune: 'Jaune',
  rouge: 'Rouge',
  rose: 'Rose',
};

const NOYAU_CSS: Record<NoyauKey, string> = {
  bleunuit: 'var(--color-pms-bleu-nuit)',
  bleuciel: 'var(--color-pms-bleu-ciel)',
  vert: 'var(--color-pms-vert)',
  jaune: 'var(--color-pms-jaune)',
  rouge: 'var(--color-pms-rouge)',
  rose: 'var(--color-pms-rose)',
};

/**
 * Collapsible filter panel for the sidebar: noyau (dominant) swatches, a tag
 * combobox, and revised-date presets. Reads and writes the active filters via
 * the words-nav context. Multi-select dimensions combine as OR; the three
 * dimensions combine as AND (handled server-side).
 */
export function WordsFilterPanel() {
  const nav = useWordsNav();
  const { noyaux, tags, revisedPreset, toCheckOnly } = nav.filters;

  function handlePreset(preset: DatePreset) {
    // Single-select: clicking the active preset clears the date filter.
    nav.setRevisedPreset(revisedPreset === preset ? null : preset);
  }

  return (
    <div className="words-filter-panel">
      <section className="words-filter-section">
        <span className="words-filter-section__label">À vérifier</span>
        <button
          type="button"
          className={`words-to-check-filter${toCheckOnly ? ' is-on' : ''}`}
          aria-pressed={toCheckOnly}
          onClick={() => nav.setToCheckOnly(!toCheckOnly)}
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            warning
          </span>
          Mots à vérifier uniquement
        </button>
      </section>

      <section className="words-filter-section">
        <span className="words-filter-section__label">Noyau dominant</span>
        <div className="words-noyau-filter">
          {NOYAU_ORDER.map((noyau) => (
            <button
              key={noyau.key}
              type="button"
              className={`words-noyau-dot words-noyau-dot--${noyau.key}${noyaux.includes(noyau.key) ? ' is-on' : ''}`}
              aria-label={NOYAU_COLOR_LABELS[noyau.key]}
              aria-pressed={noyaux.includes(noyau.key)}
              onClick={() => nav.toggleNoyau(noyau.key)}
            />
          ))}
          <button
            type="button"
            className={`words-noyau-dot words-noyau-dot--none${noyaux.includes('none') ? ' is-on' : ''}`}
            aria-label="Non défini"
            aria-pressed={noyaux.includes('none')}
            onClick={() => nav.toggleNoyau('none')}
          />
        </div>
      </section>

      <section className="words-filter-section">
        <span className="words-filter-section__label">Tags</span>
        <TagFilterCombobox selected={tags} onChange={nav.setTagFilter} />
      </section>

      <section className="words-filter-section">
        <span className="words-filter-section__label">Révisé depuis</span>
        <div className="words-date-presets">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={`words-date-preset${revisedPreset === preset ? ' is-on' : ''}`}
              aria-pressed={revisedPreset === preset}
              onClick={() => handlePreset(preset)}
            >
              {DATE_PRESET_LABELS[preset]}
            </button>
          ))}
        </div>
      </section>

      {hasActiveFilters(nav.filters) && (
        <button type="button" className="words-filter-reset" onClick={nav.resetFilters}>
          Réinitialiser les filtres
        </button>
      )}
    </div>
  );
}

// ── Tag filter combobox ─────────────────────────────────────────────────────

interface TagFilterComboboxProps {
  /** Currently selected tag labels. */
  selected: string[];
  onChange: (tags: string[]) => void;
}

/**
 * Tag picker for the filter panel: selected tags as removable chips followed by
 * a type-to-filter autocomplete over the global tag list. Unlike the word
 * editor's combobox there is no "Créer" affordance — you can only filter by
 * tags that already exist.
 */
function TagFilterCombobox({ selected, onChange }: TagFilterComboboxProps) {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: allTags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: tagsApi.list,
    staleTime: 10 * 60_000,
  });

  const selectedSet = new Set(selected);
  const trimmed = value.trim();

  // Unselected tags matching the current input.
  const options = allTags.filter(
    (t) => !selectedSet.has(t.label) && t.label.toLowerCase().includes(trimmed.toLowerCase()),
  );
  const isOpen = open && options.length > 0;

  function add(label: string) {
    if (selectedSet.has(label)) return;
    onChange([...selected, label]);
    setValue('');
    setHighlightedIndex(0);
    setOpen(false);
    inputRef.current?.focus();
  }

  function remove(label: string) {
    onChange(selected.filter((l) => l !== label));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setOpen(true);
        setHighlightedIndex(0);
      } else {
        setHighlightedIndex((i) => Math.min(i + 1, options.length - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = options[highlightedIndex];
      if (isOpen && option) add(option.label);
    } else if (e.key === 'Escape') {
      setValue('');
      setOpen(false);
      setHighlightedIndex(0);
    }
  }

  // Resolve a selected label back to its WordTag (for the chip's noyau dot).
  function tagFor(label: string): WordTag {
    return allTags.find((t) => t.label === label) ?? { label };
  }

  return (
    <div className="words-tags">
      {selected.map((label) => {
        const tag = tagFor(label);
        return (
          <span key={label} className="words-tag words-tag--removable">
            {tag.noyauKey && (
              <span className="words-tag__dot" style={{ background: NOYAU_CSS[tag.noyauKey] }} />
            )}
            {label}
            <button
              type="button"
              className="words-tag__remove"
              onClick={() => remove(label)}
              aria-label={`Retirer le filtre « ${label} »`}
            >
              <span className="material-symbols-rounded" aria-hidden="true">
                close
              </span>
            </button>
          </span>
        );
      })}
      <div className="tag-combobox">
        <input
          ref={inputRef}
          className="tag-input"
          value={value}
          placeholder="filtrer par tag…"
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
            setHighlightedIndex(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={handleKeyDown}
          aria-label="Filtrer par tag"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-controls="tag-filter-dropdown"
          role="combobox"
        />
        {isOpen && (
          <div id="tag-filter-dropdown" className="tag-dropdown" role="menu" tabIndex={-1}>
            {options.map((tag, i) => (
              <div
                key={tag.label}
                role="menuitem"
                tabIndex={-1}
                aria-current={i === highlightedIndex ? true : undefined}
                className={`tag-dropdown__item${i === highlightedIndex ? ' is-active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(tag.label);
                }}
              >
                {tag.noyauKey && (
                  <span
                    className="words-tag__dot"
                    style={{ background: NOYAU_CSS[tag.noyauKey] }}
                  />
                )}
                {tag.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
